import React, {useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import {AddCircleOutlineOutlined, CheckCircleOutlineOutlined} from '@material-ui/icons';
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import BlockUI from '../../../shared/blockui/BlockUI.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import {criarColunaOpcoes} from '../../components/ColunaOpcoes.jsx';
import FotoPessoa from '../../components/cadastro/FotoPessoa.jsx';
import {
    montarParametrosFuncionarios,
    TAMANHO_PAGINA_FUNCIONARIOS
} from '../../components/cadastro/cadastroAtendimento.js';
import {ouTraco} from '../../components/inscricao/documentoInscricao.js';
import {PAGINACAO_PADRAO, textoSemRegistros} from '../../components/paginacao.js';
import useFetch from '../../../../hooks/useFetch.js';
import {URL_FUNCIONARIOS_PAGINADO} from '../../../../constants/index.js';
import {buttonTableCell} from '../../../../utils/styles.js';

const FILTER_OPTIONS = {filterByColumn: true};
const TABLE_CONTAINER_PROPS = {component: 'div'};

const useStyles = makeStyles(theme => ({
    ...buttonTableCell(theme),
    paper: {
        width: 800,
        maxWidth: '100%'
    },
    contentRoot: {
        overflowY: 'initial'
    },
    area: {
        minHeight: theme.spacing(12)
    },
    colunaFoto: {
        width: theme.spacing(9)
    }
}));

/*
 * Busca de funcionários (paginação e filtros por Nome e Cód. funcional no servidor).
 * Reutilizável: `onSelecionar(funcionario)` recebe a linha escolhida e, com `manterAberto`
 * falso, o diálogo fecha em seguida; com `manterAberto`, quem chama decide quando fechar.
 *
 * O DataGrid lazy NÃO chama onFetch ao montar: a busca inicial é feita aqui, ao abrir.
 */
export default function DialogSelecionarFuncionario(props) {
    const {
        open,
        onClose,
        onSelecionar,
        manterAberto = false,
        titulo = 'Selecionar funcionário',
        tituloAcao = 'Selecionar',
        ocupado = false
    } = props;
    const classes = useStyles();
    const [fetch, carregando] = useFetch({returnInnerLoadingState: true});
    const [itens, setItens] = useState([]);
    const [total, setTotal] = useState(0);
    // Muda a cada abertura: reinicia página e filtros do DataGrid.
    const [abertura, setAbertura] = useState(0);

    const consultaRef = useRef({filtros: '{}', pagina: 0, tamanho: TAMANHO_PAGINA_FUNCIONARIOS});
    const requisicaoRef = useRef(0);
    // A credencial renova a cada resposta: os callbacks usam sempre o fetch do render mais recente.
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;
    // O callback das colunas (memorizadas) lê as props atuais por ref.
    const selecionarRef = useRef(null);

    const buscar = (alteracoes = {}) => {
        consultaRef.current = {...consultaRef.current, ...alteracoes};

        const {filtros, pagina, tamanho} = consultaRef.current;
        const requisicao = ++requisicaoRef.current;

        fetchRef.current(
            URL_FUNCIONARIOS_PAGINADO,
            montarParametrosFuncionarios(filtros, pagina, tamanho),
            resultado => {
                // Resposta de uma consulta já substituída por outra mais nova: descarta.
                if (requisicao === requisicaoRef.current) {
                    setItens((resultado && resultado.resultado) || []);
                    setTotal((resultado && resultado.total) || 0);
                }
            });
    };

    const onFetch = (filtros, ordem, ascendente, pagina, tamanho) => buscar({filtros, pagina, tamanho});

    useEffect(() => {
        if (open) {
            consultaRef.current = {filtros: '{}', pagina: 0, tamanho: TAMANHO_PAGINA_FUNCIONARIOS};
            setItens([]);
            setTotal(0);
            setAbertura(valor => valor + 1);
            buscar();
        }
    }, [open]);

    selecionarRef.current = funcionario => {
        onSelecionar(funcionario);
        if (!manterAberto) {
            onClose();
        }
    };

    const colunas = useMemo(() => [
        {
            label: 'Foto',
            filterable: false,
            className: classes.colunaFoto,
            valueGetter: (valor, row) => <FotoPessoa urlFoto={row.urlFoto} nome={row.nome}/>
        },
        {label: 'Nome', field: 'nome', filterable: true, valueGetter: valor => ouTraco(valor)},
        {label: 'Cód. funcional', field: 'codFuncional', filterable: true, valueGetter: valor => ouTraco(valor)},
        criarColunaOpcoes(row => [
            {
                titulo: tituloAcao,
                icone: tituloAcao === 'Adicionar'
                    ? <AddCircleOutlineOutlined/>
                    : <CheckCircleOutlineOutlined/>,
                // Enquanto grava (Responsáveis adiciona com o diálogo aberto), evita clique repetido.
                desabilitado: ocupado,
                onClick: () => selecionarRef.current(row)
            }
        ], {className: classes.buttonTableCell})
    ], [classes, tituloAcao, ocupado]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            scroll='body'
            aria-labelledby='dialog-selecionar-funcionario-titulo'
            classes={{paper: classes.paper}}>
            <DialogTitle id='dialog-selecionar-funcionario-titulo'>{titulo}</DialogTitle>
            <DialogContent classes={{root: classes.contentRoot}}>
                <div className={classes.area}>
                    <BlockUI loading={carregando}>
                        <DataGrid
                            rows={itens}
                            columns={colunas}
                            filterOptions={FILTER_OPTIONS}
                            paginate
                            paginateOptions={PAGINACAO_PADRAO}
                            tableContainerProps={TABLE_CONTAINER_PROPS}
                            lazyLoad
                            lazyLoadOptions={{
                                fetchId: `funcionarios-${abertura}`,
                                total,
                                onFetch
                            }}
                            noRecordsText={textoSemRegistros(carregando, 'Nenhum funcionário encontrado.')}/>
                    </BlockUI>
                </div>
            </DialogContent>
            <DialogActions>
                <TextButton onClick={onClose}>Fechar</TextButton>
            </DialogActions>
        </Dialog>
    );
}

DialogSelecionarFuncionario.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSelecionar: PropTypes.func.isRequired,
    manterAberto: PropTypes.bool,
    titulo: PropTypes.string,
    /* Rótulo da ação da linha: "Selecionar" (padrão) ou "Adicionar". */
    tituloAcao: PropTypes.string,
    /* Desabilita a ação enquanto a tela grava. */
    ocupado: PropTypes.bool
};
